import { injectable, inject } from 'inversify';
import { AddItemToCartUseCase } from '../../../domain/usecases/SaleUseCases';
import type { CartRepository, ProductRepository } from '../../../domain/repositories';
import { Cart, CartItem } from '../../../domain/models';
import { TYPES } from '../../../di/types';

/**
 * Implementation of AddItemToCartUseCase
 * This contains the business logic for adding items to cart
 */
@injectable()
export class AddItemToCartUseCaseImpl implements AddItemToCartUseCase {
  constructor(
    @inject(TYPES.CartRepository) private readonly cartRepository: CartRepository,
    @inject(TYPES.ProductRepository) private readonly productRepository: ProductRepository
  ) {}

  async execute(cartId: string, item: Omit<CartItem, 'subtotal'>): Promise<Cart> {
    try {
      // Validate input parameters
      await this.validateParameters(cartId, item);

      // Get current cart
      const cart = await this.cartRepository.findById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      // Get product details to validate
      const product = await this.productRepository.findById(item.productId);
      if (!product) {
        throw new Error('Product not found');
      }

      // Validate product availability and stock
      this.validateProductAvailability(product, item.quantity);

      // Check if item already exists in cart
      const existingItemIndex = cart.items.findIndex(cartItem => cartItem.productId === item.productId);

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        const existingItem = cart.items[existingItemIndex];
        const newQuantity = existingItem.quantity + item.quantity;
        
        // Validate total quantity doesn't exceed stock
        this.validateStockAvailability(product, newQuantity);

        // Update cart item with calculated subtotal
        const updatedItems = [...cart.items];
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          productDiscountPercentage: item.productDiscountPercentage,
          productDiscountAmount: this.calculateDiscountAmount(item.unitPrice, item.productDiscountPercentage),
          subtotal: this.calculateSubtotal(item.unitPrice, newQuantity, item.productDiscountPercentage)
        };

        return await this.cartRepository.update(cartId, { items: updatedItems });
      } else {
        // Add new item to cart with calculated subtotal
        const newItem: CartItem = {
          ...item,
          productDiscountAmount: this.calculateDiscountAmount(item.unitPrice, item.productDiscountPercentage),
          subtotal: this.calculateSubtotal(item.unitPrice, item.quantity, item.productDiscountPercentage)
        };

        const updatedItems = [...cart.items, newItem];
        return await this.cartRepository.update(cartId, { items: updatedItems });
      }
    } catch (error) {
      console.error('Add item to cart failed:', error);
      throw error;
    }
  }

  private async validateParameters(cartId: string, item: Omit<CartItem, 'subtotal'>): Promise<void> {
    if (!cartId || cartId.trim().length === 0) {
      throw new Error('Cart ID is required');
    }

    if (!item.productId || item.productId.trim().length === 0) {
      throw new Error('Product ID is required');
    }

    if (!item.productName || item.productName.trim().length === 0) {
      throw new Error('Product name is required');
    }

    if (item.quantity <= 0) {
      throw new Error('Quantity must be greater than 0');
    }

    if (item.quantity > 1000) {
      throw new Error('Quantity cannot exceed 1000 items');
    }

    if (item.unitPrice <= 0) {
      throw new Error('Unit price must be greater than 0');
    }

    if (item.productDiscountPercentage < 0 || item.productDiscountPercentage > 30) {
      throw new Error('Product discount must be between 0% and 30%');
    }

    if (item.taxRate < 0 || item.taxRate > 1) {
      throw new Error('Tax rate must be between 0 and 1');
    }
  }

  private validateProductAvailability(product: any, quantity: number): void {
    if (!product.isActive) {
      throw new Error('Product is not available');
    }

    this.validateStockAvailability(product, quantity);
  }

  private validateStockAvailability(product: any, quantity: number): void {
    if (product.stock < quantity) {
      throw new Error(`Insufficient stock. Available: ${product.stock}, Requested: ${quantity}`);
    }
  }

  private calculateDiscountAmount(unitPrice: number, discountPercentage: number): number {
    return (unitPrice * discountPercentage) / 100;
  }

  private calculateSubtotal(unitPrice: number, quantity: number, discountPercentage: number): number {
    const discountAmount = this.calculateDiscountAmount(unitPrice, discountPercentage);
    const discountedPrice = unitPrice - discountAmount;
    return discountedPrice * quantity;
  }
} 